import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Code, Globe } from "lucide-react";
import { Tabs } from "@/components/ui";
import { ContentPageThemeServices, FormServices } from "@/services/bedrock";
import { URLS } from "@/utils";
import { cn } from "@/lib/utils";
import { ConfigSkeleton } from "./components/LoadingStates";
import { usePreviewPlayer } from "./components/usePreviewPlayer";
import { ReviewWidget } from "./Widget";
import { ReviewsPage } from "./ReviewsPage";

type PublicReviewsView = "widget" | "page";

const VIEW_URLS: Record<PublicReviewsView, string> = {
  widget: URLS.coach.reputation.publicReviewsWidget,
  page: URLS.coach.reputation.publicReviewsPage,
};

export function PublicReviews() {
  const history = useHistory();
  const params = useParams<{ view?: string }>();
  const view: PublicReviewsView = params.view === "page" ? "page" : "widget";
  const preview = usePreviewPlayer();
  const [visited, setVisited] = useState(() => new Set([view]));

  useEffect(() => {
    setVisited(current => (current.has(view) ? current : new Set(current).add(view)));
  }, [view]);

  const { data: formsData, isLoading: isLoadingForms } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
  });

  const { data: themesData, isLoading: isLoadingThemes } = useQuery({
    queryKey: ["contentPageThemes", { page: 1, perPage: 100 }],
    queryFn: () => ContentPageThemeServices.getPageThemes({ page: 1, perPage: 100 }),
  });

  const forms = formsData?.content || [];
  const themes = themesData?.content || [];

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={view} onValueChange={next => history.replace(VIEW_URLS[next as PublicReviewsView])}>
        <Tabs.List>
          <Tabs.Trigger value="widget" className="gap-1.5 px-3">
            <Code className="size-3.5" />
            Widget
          </Tabs.Trigger>
          <Tabs.Trigger value="page" className="gap-1.5 px-3">
            <Globe className="size-3.5" />
            Reviews Page
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs>

      {isLoadingForms || isLoadingThemes ? (
        <ConfigSkeleton />
      ) : (
        <>
          {visited.has("widget") && (
            <div className={cn(view !== "widget" && "hidden")}>
              <ReviewWidget themes={themes} forms={forms} preview={preview} />
            </div>
          )}
          {visited.has("page") && (
            <div className={cn(view !== "page" && "hidden")}>
              <ReviewsPage themes={themes} forms={forms} preview={preview} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
